import { Alert, Form, Input, Modal, Radio, Select, Typography } from 'antd';

import { ERROR_REQUIRED_FIELD } from '@app/constants';

import { SimilarClientOption } from '../components/SimilarClientOption';
import {
  NEW_CLIENT_SELECTION,
  useLeadsWriteFirstModal,
} from '../hooks/useLeadsWriteFirstModal';

import type { LeadsWriteFirstSelection } from '../hooks/useLeadsWriteFirstModal';
import type { RadioChangeEvent } from 'antd';
import type { FC } from 'react';

import './LeadsWriteFirstModal.scss';

const { Text } = Typography;

const REQUIRED_RULE = [{ required: true, message: ERROR_REQUIRED_FIELD }];

interface LeadsWriteFirstModalProps {
  open: boolean;
  onClose: () => void;
}

export const LeadsWriteFirstModal: FC<LeadsWriteFirstModalProps> = ({
  open,
  onClose,
}) => {
  const {
    form,
    integrationOptions,
    hasIntegrations,
    isIntegrationsLoading,
    similarClients,
    selection,
    isSearching,
    isSubmitting,
    isSubmitDisabled,
    onSelectionChange,
    onFieldChange,
    onFinish,
    submit,
    close,
  } = useLeadsWriteFirstModal({ open, onClose });

  const selectionChangeHandler = (event: RadioChangeEvent) => {
    onSelectionChange(event.target.value as LeadsWriteFirstSelection);
  };

  const hasSimilarClients = similarClients.length > 0;

  return (
    <Modal
      className="LeadsWriteFirstModal"
      open={open}
      destroyOnHidden
      width={520}
      okText="Сохранить"
      cancelText="Отмена"
      onOk={submit}
      onCancel={close}
      title={
        <div className="LeadsWriteFirstModal__title">
          <span className="LeadsWriteFirstModal__title-text">
            Написать первым
          </span>
          <span className="LeadsWriteFirstModal__title-hint">
            Выберите интеграцию и получателя, создайте нового клиента или
            откройте похожего
          </span>
        </div>
      }
      okButtonProps={{
        loading: isSubmitting,
        disabled: isSubmitDisabled,
      }}
      cancelButtonProps={{ disabled: isSubmitting }}
    >
      <div className="LeadsWriteFirstModal__content">
        {!isIntegrationsLoading && !hasIntegrations ? (
          <Alert
            type="info"
            showIcon
            message="Нет доступных интеграций для отправки первого сообщения"
          />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="LeadsWriteFirstModal__form"
          >
            <div className="LeadsWriteFirstModal__card">
              <Text className="LeadsWriteFirstModal__section-title">
                Отправитель
              </Text>

              <Form.Item
                label="От кого"
                name="integrationId"
                rules={REQUIRED_RULE}
                className="LeadsWriteFirstModal__field"
              >
                <Select
                  showSearch
                  placeholder="Выберите интеграцию"
                  optionFilterProp="label"
                  loading={isIntegrationsLoading}
                  options={integrationOptions}
                  onChange={onFieldChange}
                />
              </Form.Item>

              <div className="LeadsWriteFirstModal__row">
                <Form.Item
                  label="Кому"
                  name="userName"
                  rules={REQUIRED_RULE}
                  className="LeadsWriteFirstModal__field"
                >
                  <Input
                    placeholder="Username / аккаунт / телефон"
                    onChange={onFieldChange}
                  />
                </Form.Item>

                <Form.Item
                  label="Имя клиента"
                  name="clientName"
                  className="LeadsWriteFirstModal__field"
                >
                  <Input placeholder="Необязательно" onChange={onFieldChange} />
                </Form.Item>
              </div>
            </div>

            <div className="LeadsWriteFirstModal__card">
              <Text className="LeadsWriteFirstModal__section-title">
                Клиент
              </Text>

              {hasSimilarClients && (
                <Alert
                  className="LeadsWriteFirstModal__warning"
                  type="warning"
                  showIcon
                  message="Найдены похожие клиенты"
                />
              )}

              <Radio.Group
                className="LeadsWriteFirstModal__selection"
                value={selection}
                onChange={selectionChangeHandler}
              >
                <Radio
                  className="LeadsWriteFirstModal__new-client"
                  value={NEW_CLIENT_SELECTION}
                >
                  Новый клиент
                </Radio>

                {similarClients.map((client) => (
                  <SimilarClientOption key={client.Id} client={client} />
                ))}
              </Radio.Group>

              {isSearching && (
                <span className="LeadsWriteFirstModal__searching">
                  Поиск похожих клиентов…
                </span>
              )}
            </div>
          </Form>
        )}
      </div>
    </Modal>
  );
};
